import React, { useState, useEffect } from 'react';
import { EvolutionInstancesList } from './EvolutionInstancesList';
import { EvolutionInstanceForm } from './EvolutionInstanceForm';
import { evolutionInstancesApi, buildEvolutionWebhookUrl } from '../../utils/admin/evolutionApi';
import { EvolutionInstance } from '../../types/admin';
import './AdminStyles.css';

// Painel autocontido das instâncias Evolution: gerencia o próprio estado/listagem/CRUD,
// usando os componentes dedicados EvolutionInstancesList e EvolutionInstanceForm.
export const EvolutionAccountsPanel: React.FC = () => {
  const [instances, setInstances] = useState<EvolutionInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [editing, setEditing] = useState<EvolutionInstance | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [testingId, setTestingId] = useState<number | null>(null);
  // URL global do webhook (a mesma para todas as instâncias) devolvida pelo backend.
  const [webhookUrl, setWebhookUrl] = useState<string>('');

  const loadInstances = async (page: number = 0) => {
    try {
      setLoading(true);
      setError('');
      const data = await evolutionInstancesApi.list(page, 20);
      setInstances(data.instances ?? []);
      setWebhookUrl(buildEvolutionWebhookUrl(data.webhook_url));
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar instâncias');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstances(0);
  }, []);

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCreate = async (formData: any) => {
    try {
      setSubmitting(true);
      setError('');
      const created = await evolutionInstancesApi.create(formData);
      flashSuccess('Instância Evolution criada com sucesso');
      setShowForm(false);
      await loadInstances(0);
      // Mostra a URL do webhook GLOBAL (a mesma para todas as instâncias) para configurar no Evolution.
      const createdWebhookUrl = buildEvolutionWebhookUrl(created.webhook_url);
      if (createdWebhookUrl) {
        window.alert('Configure este webhook GLOBAL (evento messages.upsert) no servidor Evolution.\nÉ a mesma URL para todas as instâncias:\n\n' + createdWebhookUrl);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar instância');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (formData: any) => {
    if (!editing) return;
    try {
      setSubmitting(true);
      setError('');
      await evolutionInstancesApi.update(editing.id, formData);
      flashSuccess('Instância atualizada com sucesso');
      setEditing(undefined);
      setShowForm(false);
      await loadInstances(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar instância');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setError('');
      await evolutionInstancesApi.delete(id);
      flashSuccess('Instância desativada com sucesso');
      await loadInstances(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao deletar instância');
    }
  };

  const handleToggle = async (id: number) => {
    try {
      setError('');
      await evolutionInstancesApi.toggle(id);
      await loadInstances(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alternar status');
    }
  };

  const handleTest = async (id: number) => {
    try {
      setError('');
      setTestingId(id);
      const result = await evolutionInstancesApi.test(id);
      flashSuccess(`Mensagem de teste enviada para ${result.to}. Confira a conversa "Você" no WhatsApp.`);
    } catch (err: any) {
      // O backend devolve o motivo real em { error } (api_key errada, instância offline, etc.).
      let msg = 'Falha ao testar a instância';
      try {
        const body = err?.target?.responseText ? JSON.parse(err.target.responseText) : null;
        if (body?.error) msg = body.error;
      } catch { /* mantém msg padrão */ }
      if (err instanceof Error && err.message) msg = err.message;
      setError(msg);
    } finally {
      setTestingId(null);
    }
  };

  const handleEdit = (instance: EvolutionInstance) => {
    setEditing(instance);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(undefined);
  };

  return (
    <div className="tab-content">
      {error && <div className="error-message">⚠️ {error}</div>}
      {success && <div className="success-message">✓ {success}</div>}

      <div className="content-section">
        <h3 className="section-title">Instâncias Evolution</h3>

        {webhookUrl && (
          <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span>🔗 Webhook GLOBAL (configure 1x no Evolution, evento <b>messages.upsert</b>, vale para todas as instâncias):</span>
            <code style={{ wordBreak: 'break-all' }}>{webhookUrl}</code>
            <button
              className="btn-edit"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(webhookUrl);
                  window.alert('URL do webhook copiada:\n\n' + webhookUrl);
                } catch {
                  window.prompt('Copie a URL do webhook:', webhookUrl);
                }
              }}
              title="Copiar URL do webhook global"
            >
              📋 Copiar
            </button>
          </div>
        )}

        {!showForm && (
          <button className="add-account-button" onClick={() => setShowForm(true)}>
            + Adicionar Instância
          </button>
        )}

        <div style={{ marginTop: '20px' }}>
          <EvolutionInstancesList
            instances={instances}
            loading={loading}
            testingId={testingId}
            webhookUrl={webhookUrl}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggle={handleToggle}
            onTest={handleTest}
          />
        </div>
      </div>

      <div className="content-section">
        {showForm ? (
          <EvolutionInstanceForm
            instance={editing}
            onSubmit={editing ? handleUpdate : handleCreate}
            onCancel={handleCancel}
            loading={submitting}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <p>Selecione uma instância para editar ou clique em "Adicionar Instância"</p>
          </div>
        )}
      </div>
    </div>
  );
};
